import { useMemo, useState, type FormEvent } from 'react'
import { api } from '@/api/client'
import { useFetch, useMutate } from '@/hooks/useApi'
import { useConfigQuery } from '@/context/ClubContext'
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Icon,
  Loading,
  Modal,
  PageHead,
  SectionHead
} from '@/components/ui'
import { ClubGate } from '@/components/ClubGate'
import { count, dateOnly, dateInputToMs, money, msToDateInput } from '@/lib/format'
import { Cur } from '@/lib/currency'

// Складской контур ресторанного режима (§6 плана): номенклатура, поставщики,
// приходные накладные, инвентаризация. Техкарты редактируются на карточке блюда
// (страница «Меню бара»), чтобы состав правился там, где живёт само блюдо.

type Tab = 'items' | 'suppliers' | 'invoices' | 'stockTakes' | 'reports'

type Query = ReturnType<typeof useConfigQuery>

const UNIT_LABEL: Record<string, string> = {
  pcs: 'шт',
  kg: 'кг',
  g: 'г',
  l: 'л',
  ml: 'мл'
}

const SECTION_LABEL: Record<string, string> = { bar: 'Бар', kitchen: 'Кухня' }

interface StockItem {
  id: string
  name: string
  section: 'bar' | 'kitchen'
  unit: string
  barcode: string | null
  minQty: number
  qty: number
  costPrice: number
  isSemiFinished: boolean
  isStopped: boolean
  stopReason: string | null
  isActive: boolean
}

interface Supplier {
  id: string
  name: string
  contact: string | null
  isActive: boolean
}

interface InvoiceLine {
  id: string
  stockItemId: string
  qty: number
  unit: string
  priceExVat: number
  vatRate: number
  priceIncVat: number
  sumExVat: number
  sumIncVat: number
}

interface Invoice {
  id: string
  supplierId: string
  externalNumber: string | null
  invoiceDate: number
  status: 'draft' | 'posted'
  totalExVat: number
  totalVat: number
  totalIncVat: number
  postedAt: number | null
  lines?: InvoiceLine[]
}

interface StockTakeLine {
  id: string
  stockItemId: string
  name: string
  unit: string
  costPrice: number
  qtyExpected: number
  qtyActual: number | null
  diffQty: number | null
  diffSum: number | null
}

interface StockTake {
  id: string
  status: 'open' | 'closed'
  startedAt: number
  closedAt: number | null
  lines?: StockTakeLine[]
  progress?: { total: number; counted: number }
  diffSum?: number
}

export function Warehouse(): React.JSX.Element {
  const [tab, setTab] = useState<Tab>('items')
  return (
    <>
      <PageHead
        eyebrow="Конфигурация"
        title="Склад бара и кухни"
        lead="Складская номенклатура с себестоимостью, поставщики, приходные накладные с НДС и инвентаризация. Себестоимость пересчитывается по средневзвешенной цене при проведении накладной; состав блюд задаётся техкартой на карточке блюда в разделе «Меню бара»."
      />
      <ClubGate>
        <div className="toolbar">
          <div className="tabs">
            <button
              className={`tab${tab === 'items' ? ' is-active' : ''}`}
              onClick={() => setTab('items')}
            >
              Номенклатура
            </button>
            <button
              className={`tab${tab === 'suppliers' ? ' is-active' : ''}`}
              onClick={() => setTab('suppliers')}
            >
              Поставщики
            </button>
            <button
              className={`tab${tab === 'invoices' ? ' is-active' : ''}`}
              onClick={() => setTab('invoices')}
            >
              Приходные накладные
            </button>
            <button
              className={`tab${tab === 'stockTakes' ? ' is-active' : ''}`}
              onClick={() => setTab('stockTakes')}
            >
              Инвентаризация
            </button>
            <button
              className={`tab${tab === 'reports' ? ' is-active' : ''}`}
              onClick={() => setTab('reports')}
            >
              Отчёты
            </button>
          </div>
        </div>

        {tab === 'items' && <ItemsSection />}
        {tab === 'suppliers' && <SuppliersSection />}
        {tab === 'invoices' && <InvoicesSection />}
        {tab === 'stockTakes' && <StockTakesSection />}
        {tab === 'reports' && <ReportsSection />}
      </ClubGate>
    </>
  )
}

// ─────────────────────────── Номенклатура ───────────────────────────

function ItemsSection(): React.JSX.Element {
  const query = useConfigQuery()
  const list = useFetch<StockItem[]>('/v1/warehouse/items', query, [query?.clubId])
  const [editing, setEditing] = useState<StockItem | 'new' | null>(null)
  const [adjusting, setAdjusting] = useState<StockItem | null>(null)
  const [stopping, setStopping] = useState<StockItem | null>(null)
  const stop = useMutate()

  const rows = list.data ?? []
  const lowStock = rows.filter((r) => r.isActive && r.qty <= r.minQty).length

  const onStopConfirm = async (): Promise<void> => {
    if (!stopping) return
    const ok = await stop.mutate(() =>
      api.patch(
        `/v1/warehouse/items/${stopping.id}/stop`,
        { isStopped: !stopping.isStopped },
        { query }
      )
    )
    if (ok) {
      setStopping(null)
      list.reload()
    }
  }

  return (
    <>
      <div className="toolbar">
        <div className="toolbar__left">
          <span className="muted">{rows.length} позиц.</span>
          {lowStock > 0 && <span className="muted"> · {lowStock} на минимуме или ниже</span>}
        </div>
        <div className="toolbar__right">
          <button className="btn btn--primary btn--sm" onClick={() => setEditing('new')}>
            {Icon.plus}
            Новая позиция
          </button>
        </div>
      </div>

      {list.loading && <Loading />}
      {list.error && <ErrorState message={list.error} />}
      {!list.loading && !list.error && rows.length === 0 && (
        <EmptyState message="Номенклатура пуста. Добавьте ингредиенты и товары, из которых состоят блюда." />
      )}

      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>Название</th>
                <th>Секция</th>
                <th className="num">Остаток</th>
                <th className="num">Минимум</th>
                <th className="num">Себестоимость</th>
                <th>Штрихкод</th>
                <th>Состояние</th>
                <th style={{ width: 128 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((it) => (
                <tr key={it.id}>
                  <td>
                    {it.name}
                    {it.isSemiFinished && <span className="muted"> · полуфабрикат</span>}
                  </td>
                  <td>{SECTION_LABEL[it.section] ?? it.section}</td>
                  <td className="num">
                    {/* Отрицательный остаток допустим: продажа не блокируется,
                        минус — сигнал к инвентаризации. */}
                    <span style={it.qty < 0 ? { color: 'var(--red)' } : undefined}>
                      {count(it.qty)}
                    </span>{' '}
                    <span className="muted">{UNIT_LABEL[it.unit] ?? it.unit}</span>
                  </td>
                  <td className="num">{count(it.minQty)}</td>
                  <td className="num">
                    {money(it.costPrice)} <Cur />
                  </td>
                  <td>{it.barcode ?? '—'}</td>
                  <td>
                    {!it.isActive ? (
                      <span className="status">
                        <span className="dot dot--off" />
                        архив
                      </span>
                    ) : it.isStopped ? (
                      <span className="status" title={it.stopReason ?? undefined}>
                        <span className="dot dot--off" />
                        стоп
                      </span>
                    ) : (
                      <span className="status">
                        <span className="dot dot--on" />в работе
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="row-actions">
                      <button
                        className="btn btn--ghost btn--sm"
                        title="Скорректировать остаток"
                        onClick={() => setAdjusting(it)}
                      >
                        Остаток
                      </button>
                      <button
                        className="btn btn--ghost btn--sm"
                        title={it.isStopped ? 'Снять стоп' : 'Поставить на стоп'}
                        onClick={() => setStopping(it)}
                      >
                        {it.isStopped ? 'Снять стоп' : 'Стоп'}
                      </button>
                      <button className="icon-btn" title="Изменить" onClick={() => setEditing(it)}>
                        {Icon.edit}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <ItemForm
          item={editing === 'new' ? null : editing}
          query={query}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            list.reload()
          }}
        />
      )}

      {adjusting && (
        <AdjustForm
          item={adjusting}
          query={query}
          onClose={() => setAdjusting(null)}
          onSaved={() => {
            setAdjusting(null)
            list.reload()
          }}
        />
      )}

      {stopping && (
        <ConfirmDialog
          title={stopping.isStopped ? 'Снять стоп' : 'Поставить на стоп'}
          message={
            stopping.isStopped
              ? `Снять стоп с «${stopping.name}»? Блюда с этим ингредиентом снова станут доступны.`
              : `Поставить «${stopping.name}» на стоп? Все блюда, где этот ингредиент есть в техкарте, станут недоступны.`
          }
          busy={stop.saving}
          error={stop.error}
          onConfirm={() => void onStopConfirm()}
          onCancel={() => setStopping(null)}
        />
      )}
    </>
  )
}

function ItemForm({
  item,
  query,
  onClose,
  onSaved
}: {
  item: StockItem | null
  query: Query
  onClose: () => void
  onSaved: () => void
}): React.JSX.Element {
  const isEdit = item !== null
  const [name, setName] = useState(item?.name ?? '')
  const [section, setSection] = useState<'bar' | 'kitchen'>(item?.section ?? 'bar')
  const [unit, setUnit] = useState(item?.unit ?? 'pcs')
  const [barcode, setBarcode] = useState(item?.barcode ?? '')
  const [minQty, setMinQty] = useState(item ? String(item.minQty) : '')
  const [isSemiFinished, setIsSemiFinished] = useState(item?.isSemiFinished ?? false)
  const [isActive, setIsActive] = useState(item?.isActive ?? true)
  const [validation, setValidation] = useState<string | null>(null)
  const save = useMutate()

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) return setValidation('Укажите название позиции.')
    const minNum = minQty === '' ? 0 : Number(minQty)
    if (!Number.isFinite(minNum) || minNum < 0) {
      return setValidation('Минимальный остаток должен быть неотрицательным числом.')
    }
    const code = barcode.trim()
    if (code !== '' && code.length < 4) return setValidation('Штрихкод слишком короткий.')
    setValidation(null)

    const body = {
      name: trimmed,
      section,
      unit,
      barcode: code === '' ? null : code,
      minQty: minNum,
      isSemiFinished,
      isActive
    }
    const ok = await save.mutate(() =>
      isEdit
        ? api.patch(`/v1/warehouse/items/${item.id}`, body, { query })
        : api.post('/v1/warehouse/items', body, { query })
    )
    if (ok) onSaved()
  }

  return (
    <Modal
      title={isEdit ? 'Изменить позицию склада' : 'Новая позиция склада'}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn--ghost" onClick={onClose} type="button" disabled={save.saving}>
            Отмена
          </button>
          <button
            className="btn btn--primary"
            type="submit"
            form="stock-form"
            disabled={save.saving}
          >
            {save.saving && <span className="spin" />}
            Сохранить
          </button>
        </>
      }
    >
      <form id="stock-form" onSubmit={(e) => void onSubmit(e)}>
        <div className="form-grid">
          <div className="field field--full">
            <label className="field__label" htmlFor="w-name">
              Название
            </label>
            <input
              id="w-name"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="напр. Кофе зерно"
              autoFocus
            />
          </div>
          <div className="field">
            <label className="field__label" htmlFor="w-section">
              Секция
            </label>
            <select
              id="w-section"
              className="input"
              value={section}
              onChange={(e) => setSection(e.target.value as 'bar' | 'kitchen')}
            >
              <option value="bar">Бар</option>
              <option value="kitchen">Кухня</option>
            </select>
          </div>
          <div className="field">
            <label className="field__label" htmlFor="w-unit">
              Единица измерения
            </label>
            <select
              id="w-unit"
              className="input"
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
            >
              {Object.entries(UNIT_LABEL).map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label className="field__label" htmlFor="w-min">
              Минимальный остаток
            </label>
            <input
              id="w-min"
              className="input"
              type="number"
              step="0.001"
              min="0"
              value={minQty}
              onChange={(e) => setMinQty(e.target.value)}
              placeholder="0"
            />
          </div>
          <div className="field">
            <label className="field__label" htmlFor="w-barcode">
              Штрихкод
            </label>
            <input
              id="w-barcode"
              className="input"
              value={barcode}
              onChange={(e) => setBarcode(e.target.value)}
              placeholder="сканером или вручную"
            />
          </div>
          <div className="field field--full">
            <label className="checkbox">
              <input
                type="checkbox"
                checked={isSemiFinished}
                onChange={(e) => setIsSemiFinished(e.target.checked)}
              />
              Полуфабрикат (готовится из других позиций склада)
            </label>
          </div>
          <div className="field field--full">
            <label className="checkbox">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              Активна (участвует в инвентаризации и подсказках)
            </label>
          </div>
        </div>
        {validation && <div className="form-error">{validation}</div>}
        {save.error && <div className="form-error">{save.error}</div>}
        <p className="muted" style={{ marginTop: 8 }}>
          Остаток и себестоимость здесь не редактируются: остаток меняется приходными накладными,
          корректировкой и инвентаризацией, себестоимость считается по средневзвешенной цене
          приходов.
        </p>
      </form>
    </Modal>
  )
}

function AdjustForm({
  item,
  query,
  onClose,
  onSaved
}: {
  item: StockItem
  query: Query
  onClose: () => void
  onSaved: () => void
}): React.JSX.Element {
  const [qty, setQty] = useState(String(item.qty))
  const [comment, setComment] = useState('')
  const [validation, setValidation] = useState<string | null>(null)
  const save = useMutate()

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    const num = Number(qty)
    if (!Number.isFinite(num)) return setValidation('Остаток должен быть числом.')
    setValidation(null)
    const ok = await save.mutate(() =>
      api.post(
        `/v1/warehouse/items/${item.id}/adjust`,
        { qty: num, comment: comment.trim() || undefined },
        { query }
      )
    )
    if (ok) onSaved()
  }

  const delta = Number(qty) - item.qty

  return (
    <Modal
      title={`Корректировка остатка: ${item.name}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn--ghost" onClick={onClose} type="button" disabled={save.saving}>
            Отмена
          </button>
          <button
            className="btn btn--primary"
            type="submit"
            form="adjust-form"
            disabled={save.saving}
          >
            {save.saving && <span className="spin" />}
            Скорректировать
          </button>
        </>
      }
    >
      <form id="adjust-form" onSubmit={(e) => void onSubmit(e)}>
        <div className="form-grid">
          <div className="field">
            <label className="field__label" htmlFor="a-qty">
              Новый остаток, {UNIT_LABEL[item.unit] ?? item.unit}
            </label>
            <input
              id="a-qty"
              className="input"
              type="number"
              step="0.001"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              autoFocus
            />
          </div>
          <div className="field">
            <label className="field__label" htmlFor="a-was">
              Было
            </label>
            <input id="a-was" className="input" value={count(item.qty)} readOnly />
          </div>
          <div className="field field--full">
            <label className="field__label" htmlFor="a-comment">
              Причина (попадёт в журнал движений)
            </label>
            <input
              id="a-comment"
              className="input"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="напр. бой, порча, пересорт"
            />
          </div>
        </div>
        {Number.isFinite(delta) && delta !== 0 && (
          <p className="muted" style={{ marginTop: 8 }}>
            Движение: {delta > 0 ? '+' : ''}
            {count(delta)} {UNIT_LABEL[item.unit] ?? item.unit}
          </p>
        )}
        {validation && <div className="form-error">{validation}</div>}
        {save.error && <div className="form-error">{save.error}</div>}
      </form>
    </Modal>
  )
}

// ─────────────────────────── Поставщики ───────────────────────────

function SuppliersSection(): React.JSX.Element {
  const query = useConfigQuery()
  const list = useFetch<Supplier[]>('/v1/warehouse/suppliers', query, [query?.clubId])
  const [editing, setEditing] = useState<Supplier | 'new' | null>(null)
  const rows = list.data ?? []

  return (
    <>
      <div className="toolbar">
        <div className="toolbar__left">
          <span className="muted">{rows.length} поставщ.</span>
        </div>
        <div className="toolbar__right">
          <button className="btn btn--primary btn--sm" onClick={() => setEditing('new')}>
            {Icon.plus}
            Новый поставщик
          </button>
        </div>
      </div>

      {list.loading && <Loading />}
      {list.error && <ErrorState message={list.error} />}
      {!list.loading && !list.error && rows.length === 0 && (
        <EmptyState message="Поставщиков пока нет. Без поставщика нельзя создать приходную накладную." />
      )}

      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>Название</th>
                <th>Контакт</th>
                <th>Активен</th>
                <th style={{ width: 64 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id}>
                  <td>{s.name}</td>
                  <td>{s.contact ?? '—'}</td>
                  <td>
                    <span className="status">
                      <span className={`dot dot--${s.isActive ? 'on' : 'off'}`} />
                      {s.isActive ? 'да' : 'нет'}
                    </span>
                  </td>
                  <td>
                    <button className="icon-btn" title="Изменить" onClick={() => setEditing(s)}>
                      {Icon.edit}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <SupplierForm
          supplier={editing === 'new' ? null : editing}
          query={query}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            list.reload()
          }}
        />
      )}
    </>
  )
}

function SupplierForm({
  supplier,
  query,
  onClose,
  onSaved
}: {
  supplier: Supplier | null
  query: Query
  onClose: () => void
  onSaved: () => void
}): React.JSX.Element {
  const isEdit = supplier !== null
  const [name, setName] = useState(supplier?.name ?? '')
  const [contact, setContact] = useState(supplier?.contact ?? '')
  const [isActive, setIsActive] = useState(supplier?.isActive ?? true)
  const [validation, setValidation] = useState<string | null>(null)
  const save = useMutate()

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) return setValidation('Укажите название поставщика.')
    setValidation(null)
    const body = { name: trimmed, contact: contact.trim() || undefined, isActive }
    const ok = await save.mutate(() =>
      isEdit
        ? api.patch(`/v1/warehouse/suppliers/${supplier.id}`, body, { query })
        : api.post('/v1/warehouse/suppliers', body, { query })
    )
    if (ok) onSaved()
  }

  return (
    <Modal
      title={isEdit ? 'Изменить поставщика' : 'Новый поставщик'}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn--ghost" onClick={onClose} type="button" disabled={save.saving}>
            Отмена
          </button>
          <button
            className="btn btn--primary"
            type="submit"
            form="supplier-form"
            disabled={save.saving}
          >
            {save.saving && <span className="spin" />}
            Сохранить
          </button>
        </>
      }
    >
      <form id="supplier-form" onSubmit={(e) => void onSubmit(e)}>
        <div className="form-grid">
          <div className="field field--full">
            <label className="field__label" htmlFor="s-name">
              Название
            </label>
            <input
              id="s-name"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="напр. ООО «Кофе Импорт»"
              autoFocus
            />
          </div>
          <div className="field field--full">
            <label className="field__label" htmlFor="s-contact">
              Контакт
            </label>
            <input
              id="s-contact"
              className="input"
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              placeholder="телефон, email или ФИО менеджера"
            />
          </div>
          <div className="field field--full">
            <label className="checkbox">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              Активен
            </label>
          </div>
        </div>
        {validation && <div className="form-error">{validation}</div>}
        {save.error && <div className="form-error">{save.error}</div>}
      </form>
    </Modal>
  )
}

// ─────────────────────── Приходные накладные ───────────────────────

function InvoicesSection(): React.JSX.Element {
  const query = useConfigQuery()
  const list = useFetch<Invoice[]>('/v1/warehouse/invoices', query, [query?.clubId])
  const suppliers = useFetch<Supplier[]>('/v1/warehouse/suppliers', query, [query?.clubId])
  const items = useFetch<StockItem[]>('/v1/warehouse/items', query, [query?.clubId])
  const [editing, setEditing] = useState<Invoice | 'new' | null>(null)
  const [posting, setPosting] = useState<Invoice | null>(null)
  const [deleting, setDeleting] = useState<Invoice | null>(null)
  const post = useMutate()
  const del = useMutate()

  const rows = list.data ?? []
  const supplierName = useMemo(() => {
    const map = new Map((suppliers.data ?? []).map((s) => [s.id, s.name]))
    return (id: string): string => map.get(id) ?? '—'
  }, [suppliers.data])

  const onPostConfirm = async (): Promise<void> => {
    if (!posting) return
    const ok = await post.mutate(() =>
      api.post(`/v1/warehouse/invoices/${posting.id}/post`, undefined, { query })
    )
    if (ok) {
      setPosting(null)
      list.reload()
      items.reload()
    }
  }

  const onDeleteConfirm = async (): Promise<void> => {
    if (!deleting) return
    const ok = await del.mutate(() => api.del(`/v1/warehouse/invoices/${deleting.id}`, { query }))
    if (ok) {
      setDeleting(null)
      list.reload()
    }
  }

  // Накладную нельзя оформить без справочников: нужен поставщик и хотя бы одна
  // позиция номенклатуры. Пока они грузятся или не загрузились — кнопка тоже
  // недоступна, поэтому причину показываем текстом, а не только в title.
  const depsLoading = suppliers.loading || items.loading
  const depsError = suppliers.error ?? items.error
  const missing: string[] = []
  if (!depsLoading && depsError === null) {
    if ((suppliers.data ?? []).length === 0) missing.push('поставщика (вкладка «Поставщики»)')
    if ((items.data ?? []).length === 0)
      missing.push('позицию номенклатуры (вкладка «Номенклатура»)')
  }
  const canCreate = !depsLoading && depsError === null && missing.length === 0
  const blockReason = depsError
    ? `Не удалось загрузить справочники поставщиков и номенклатуры: ${depsError}`
    : missing.length > 0
      ? `Сначала добавьте ${missing.join(' и ')}.`
      : null

  return (
    <>
      <div className="toolbar">
        <div className="toolbar__left">
          <span className="muted">{rows.length} накладн.</span>
        </div>
        <div className="toolbar__right">
          <button
            className="btn btn--primary btn--sm"
            onClick={() => setEditing('new')}
            disabled={!canCreate}
            title={blockReason ?? undefined}
          >
            {Icon.plus}
            Новая накладная
          </button>
        </div>
      </div>

      {blockReason &&
        (depsError ? <ErrorState message={blockReason} /> : <EmptyState message={blockReason} />)}

      {list.loading && <Loading />}
      {list.error && <ErrorState message={list.error} />}
      {!list.loading && !list.error && rows.length === 0 && (
        <EmptyState message="Накладных пока нет. Черновик можно править и удалять, проведённую — нет." />
      )}

      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>Дата</th>
                <th>Номер</th>
                <th>Поставщик</th>
                <th className="num">Без НДС</th>
                <th className="num">НДС</th>
                <th className="num">Итого</th>
                <th>Статус</th>
                <th style={{ width: 168 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((inv) => (
                <tr key={inv.id}>
                  <td>{dateOnly(inv.invoiceDate)}</td>
                  <td>{inv.externalNumber ?? '—'}</td>
                  <td>{supplierName(inv.supplierId)}</td>
                  <td className="num">
                    {money(inv.totalExVat)} <Cur />
                  </td>
                  <td className="num">
                    {money(inv.totalVat)} <Cur />
                  </td>
                  <td className="num">
                    {money(inv.totalIncVat)} <Cur />
                  </td>
                  <td>
                    <span className="status">
                      <span className={`dot dot--${inv.status === 'posted' ? 'on' : 'off'}`} />
                      {inv.status === 'posted' ? 'проведена' : 'черновик'}
                    </span>
                  </td>
                  <td>
                    <div className="row-actions">
                      <button
                        className="btn btn--ghost btn--sm"
                        onClick={() => setEditing(inv)}
                        title={inv.status === 'posted' ? 'Посмотреть' : 'Изменить'}
                      >
                        {inv.status === 'posted' ? 'Открыть' : 'Изменить'}
                      </button>
                      {inv.status === 'draft' && (
                        <>
                          <button
                            className="btn btn--primary btn--sm"
                            onClick={() => setPosting(inv)}
                          >
                            Провести
                          </button>
                          <button
                            className="icon-btn icon-btn--danger"
                            title="Удалить черновик"
                            onClick={() => setDeleting(inv)}
                          >
                            {Icon.trash}
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <InvoiceForm
          invoice={editing === 'new' ? null : editing}
          suppliers={suppliers.data ?? []}
          items={items.data ?? []}
          query={query}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            list.reload()
          }}
        />
      )}

      {posting && (
        <ConfirmDialog
          title="Провести накладную"
          message="После проведения накладную нельзя изменить или удалить: остатки увеличатся, себестоимость позиций пересчитается по средневзвешенной цене, а себестоимость блюд с этими ингредиентами обновится."
          busy={post.saving}
          error={post.error}
          onConfirm={() => void onPostConfirm()}
          onCancel={() => setPosting(null)}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title="Удалить черновик"
          message="Удалить черновик накладной? Склад не изменится."
          busy={del.saving}
          error={del.error}
          onConfirm={() => void onDeleteConfirm()}
          onCancel={() => setDeleting(null)}
        />
      )}
    </>
  )
}

interface DraftLine {
  stockItemId: string
  qty: string
  priceExVat: string
  vatRate: string
}

function InvoiceForm({
  invoice,
  suppliers,
  items,
  query,
  onClose,
  onSaved
}: {
  invoice: Invoice | null
  suppliers: Supplier[]
  items: StockItem[]
  query: Query
  onClose: () => void
  onSaved: () => void
}): React.JSX.Element {
  const isEdit = invoice !== null
  const readOnly = invoice?.status === 'posted'
  // Полная накладная (со строками) грузится по id — список отдаёт только шапки.
  const full = useFetch<Invoice>(isEdit ? `/v1/warehouse/invoices/${invoice.id}` : null, query, [
    invoice?.id
  ])

  const [supplierId, setSupplierId] = useState(invoice?.supplierId ?? suppliers[0]?.id ?? '')
  const [externalNumber, setExternalNumber] = useState(invoice?.externalNumber ?? '')
  const [invoiceDate, setInvoiceDate] = useState(msToDateInput(invoice?.invoiceDate ?? Date.now()))
  const [lines, setLines] = useState<DraftLine[]>([
    { stockItemId: items[0]?.id ?? '', qty: '', priceExVat: '', vatRate: '20' }
  ])
  const [loadedId, setLoadedId] = useState<string | null>(null)
  const [validation, setValidation] = useState<string | null>(null)
  const save = useMutate()

  // Подтягиваем строки существующей накладной один раз после загрузки.
  if (full.data && loadedId !== full.data.id) {
    setLoadedId(full.data.id)
    setLines(
      (full.data.lines ?? []).map((l) => ({
        stockItemId: l.stockItemId,
        qty: String(l.qty),
        priceExVat: String(l.priceExVat),
        vatRate: String(l.vatRate)
      }))
    )
  }

  const itemName = (id: string): string => items.find((i) => i.id === id)?.name ?? '—'

  const totals = lines.reduce(
    (acc, l) => {
      const qty = Number(l.qty)
      const price = Number(l.priceExVat)
      const vat = Number(l.vatRate)
      if (!Number.isFinite(qty) || !Number.isFinite(price) || !Number.isFinite(vat)) return acc
      const sumEx = qty * price
      const sumInc = qty * (price + (price * vat) / 100)
      return { ex: acc.ex + sumEx, inc: acc.inc + sumInc }
    },
    { ex: 0, inc: 0 }
  )

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    if (!supplierId) return setValidation('Выберите поставщика.')
    const prepared = []
    for (const l of lines) {
      if (!l.stockItemId) return setValidation('В строке не выбрана позиция склада.')
      const qty = Number(l.qty)
      const price = Number(l.priceExVat)
      const vat = l.vatRate === '' ? 0 : Number(l.vatRate)
      if (!Number.isFinite(qty) || qty <= 0) {
        return setValidation(`«${itemName(l.stockItemId)}»: количество должно быть больше нуля.`)
      }
      if (!Number.isFinite(price) || price < 0) {
        return setValidation(`«${itemName(l.stockItemId)}»: цена без НДС некорректна.`)
      }
      if (!Number.isFinite(vat) || vat < 0 || vat > 100) {
        return setValidation(`«${itemName(l.stockItemId)}»: ставка НДС от 0 до 100.`)
      }
      prepared.push({ stockItemId: l.stockItemId, qty, priceExVat: price, vatRate: vat })
    }
    if (prepared.length === 0) return setValidation('Добавьте хотя бы одну строку.')
    setValidation(null)

    const body = {
      supplierId,
      externalNumber: externalNumber.trim() || undefined,
      invoiceDate: dateInputToMs(invoiceDate),
      lines: prepared
    }
    const ok = await save.mutate(() =>
      isEdit
        ? api.patch(`/v1/warehouse/invoices/${invoice.id}`, body, { query })
        : api.post('/v1/warehouse/invoices', body, { query })
    )
    if (ok) onSaved()
  }

  const setLine = (idx: number, patch: Partial<DraftLine>): void =>
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)))

  return (
    <Modal
      title={readOnly ? 'Проведённая накладная' : isEdit ? 'Изменить накладную' : 'Новая накладная'}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn--ghost" onClick={onClose} type="button" disabled={save.saving}>
            {readOnly ? 'Закрыть' : 'Отмена'}
          </button>
          {!readOnly && (
            <button
              className="btn btn--primary"
              type="submit"
              form="invoice-form"
              disabled={save.saving}
            >
              {save.saving && <span className="spin" />}
              Сохранить черновик
            </button>
          )}
        </>
      }
    >
      {full.loading && <Loading />}
      <form id="invoice-form" onSubmit={(e) => void onSubmit(e)}>
        <div className="form-grid">
          <div className="field">
            <label className="field__label" htmlFor="inv-supplier">
              Поставщик
            </label>
            <select
              id="inv-supplier"
              className="input"
              value={supplierId}
              onChange={(e) => setSupplierId(e.target.value)}
              disabled={readOnly}
            >
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label className="field__label" htmlFor="inv-num">
              Номер накладной
            </label>
            <input
              id="inv-num"
              className="input"
              value={externalNumber}
              onChange={(e) => setExternalNumber(e.target.value)}
              placeholder="как в документе поставщика"
              disabled={readOnly}
            />
          </div>
          <div className="field">
            <label className="field__label" htmlFor="inv-date">
              Дата
            </label>
            <input
              id="inv-date"
              className="input"
              type="date"
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
              disabled={readOnly}
            />
          </div>
        </div>

        <div className="table-wrap" style={{ marginTop: 12 }}>
          <table className="rtable">
            <thead>
              <tr>
                <th>Позиция</th>
                <th className="num" style={{ width: 110 }}>
                  Кол-во
                </th>
                <th className="num" style={{ width: 130 }}>
                  Цена без НДС
                </th>
                <th className="num" style={{ width: 100 }}>
                  НДС, %
                </th>
                <th className="num" style={{ width: 120 }}>
                  Сумма с НДС
                </th>
                {!readOnly && <th style={{ width: 48 }} />}
              </tr>
            </thead>
            <tbody>
              {lines.map((l, idx) => {
                const qty = Number(l.qty)
                const price = Number(l.priceExVat)
                const vat = l.vatRate === '' ? 0 : Number(l.vatRate)
                const sumInc =
                  Number.isFinite(qty) && Number.isFinite(price) && Number.isFinite(vat)
                    ? qty * (price + (price * vat) / 100)
                    : 0
                return (
                  <tr key={idx}>
                    <td>
                      <select
                        className="input"
                        value={l.stockItemId}
                        onChange={(e) => setLine(idx, { stockItemId: e.target.value })}
                        disabled={readOnly}
                      >
                        <option value="">— выберите —</option>
                        {items.map((it) => (
                          <option key={it.id} value={it.id}>
                            {it.name} ({UNIT_LABEL[it.unit] ?? it.unit})
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="num">
                      <input
                        className="input"
                        type="number"
                        step="0.001"
                        min="0"
                        value={l.qty}
                        onChange={(e) => setLine(idx, { qty: e.target.value })}
                        disabled={readOnly}
                      />
                    </td>
                    <td className="num">
                      <input
                        className="input"
                        type="number"
                        step="0.0001"
                        min="0"
                        value={l.priceExVat}
                        onChange={(e) => setLine(idx, { priceExVat: e.target.value })}
                        disabled={readOnly}
                      />
                    </td>
                    <td className="num">
                      <input
                        className="input"
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={l.vatRate}
                        onChange={(e) => setLine(idx, { vatRate: e.target.value })}
                        disabled={readOnly}
                      />
                    </td>
                    <td className="num">
                      {money(sumInc)} <Cur />
                    </td>
                    {!readOnly && (
                      <td>
                        <button
                          type="button"
                          className="icon-btn icon-btn--danger"
                          title="Убрать строку"
                          onClick={() => setLines((p) => p.filter((_, i) => i !== idx))}
                          disabled={lines.length === 1}
                        >
                          {Icon.trash}
                        </button>
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {!readOnly && (
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            style={{ marginTop: 8 }}
            onClick={() =>
              setLines((p) => [
                ...p,
                { stockItemId: items[0]?.id ?? '', qty: '', priceExVat: '', vatRate: '20' }
              ])
            }
          >
            {Icon.plus}
            Добавить строку
          </button>
        )}

        <p className="muted" style={{ marginTop: 12 }}>
          Итого: без НДС {money(totals.ex)} <Cur />, с НДС {money(totals.inc)} <Cur />. Суммы
          пересчитает сервер при сохранении — себестоимость считается по цене с НДС (клуб на УСН,
          НДС к вычету не принимается).
        </p>
        {validation && <div className="form-error">{validation}</div>}
        {save.error && <div className="form-error">{save.error}</div>}
      </form>
    </Modal>
  )
}

// ─────────────────────────── Инвентаризация ───────────────────────────

function StockTakesSection(): React.JSX.Element {
  const query = useConfigQuery()
  const list = useFetch<StockTake[]>('/v1/warehouse/stock-takes', query, [query?.clubId])
  const [openId, setOpenId] = useState<string | null>(null)
  const open = useMutate()

  const rows = list.data ?? []
  const hasOpen = rows.some((r) => r.status === 'open')

  const onOpen = async (): Promise<void> => {
    const ok = await open.mutate(() => api.post('/v1/warehouse/stock-takes', undefined, { query }))
    if (ok) list.reload()
  }

  return (
    <>
      <div className="toolbar">
        <div className="toolbar__left">
          <span className="muted">{rows.length} инвентаризац.</span>
        </div>
        <div className="toolbar__right">
          <button
            className="btn btn--primary btn--sm"
            onClick={() => void onOpen()}
            disabled={hasOpen || open.saving}
            title={hasOpen ? 'Уже есть открытая инвентаризация' : undefined}
          >
            {open.saving && <span className="spin" />}
            Начать инвентаризацию
          </button>
        </div>
      </div>

      {open.error && <ErrorState message={open.error} />}
      {list.loading && <Loading />}
      {list.error && <ErrorState message={list.error} />}
      {!list.loading && !list.error && rows.length === 0 && (
        <EmptyState message="Инвентаризаций пока не было. Открытие снимет срез ожидаемых остатков по активным позициям." />
      )}

      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>Начата</th>
                <th>Закрыта</th>
                <th>Статус</th>
                <th style={{ width: 96 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.id}>
                  <td>{dateOnly(t.startedAt)}</td>
                  <td>{t.closedAt ? dateOnly(t.closedAt) : '—'}</td>
                  <td>
                    <span className="status">
                      <span className={`dot dot--${t.status === 'open' ? 'off' : 'on'}`} />
                      {t.status === 'open' ? 'открыта' : 'закрыта'}
                    </span>
                  </td>
                  <td>
                    <button className="btn btn--ghost btn--sm" onClick={() => setOpenId(t.id)}>
                      {t.status === 'open' ? 'Считать' : 'Открыть'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openId && (
        <StockTakeSheet
          id={openId}
          query={query}
          onClose={() => setOpenId(null)}
          onChanged={() => list.reload()}
        />
      )}
    </>
  )
}

function StockTakeSheet({
  id,
  query,
  onClose,
  onChanged
}: {
  id: string
  query: Query
  onClose: () => void
  onChanged: () => void
}): React.JSX.Element {
  const take = useFetch<StockTake>(`/v1/warehouse/stock-takes/${id}`, query, [id])
  const [closing, setClosing] = useState(false)
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const save = useMutate()
  const close = useMutate()

  const data = take.data
  const readOnly = data?.status === 'closed'
  const lines = data?.lines ?? []

  const saveActual = async (stockItemId: string, raw: string): Promise<void> => {
    const num = Number(raw)
    if (raw === '' || !Number.isFinite(num) || num < 0) return
    const ok = await save.mutate(() =>
      api.patch(
        `/v1/warehouse/stock-takes/${id}/actual`,
        { stockItemId, qtyActual: num },
        { query }
      )
    )
    if (ok) take.reload()
  }

  const onCloseTake = async (): Promise<void> => {
    const ok = await close.mutate(() =>
      api.post(`/v1/warehouse/stock-takes/${id}/close`, undefined, { query })
    )
    if (ok) {
      setClosing(false)
      take.reload()
      onChanged()
    }
  }

  return (
    <Modal
      title={readOnly ? 'Инвентаризация (закрыта)' : 'Инвентаризация: подсчёт'}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn--ghost" onClick={onClose} type="button">
            Закрыть окно
          </button>
          {!readOnly && (
            <button
              className="btn btn--primary"
              type="button"
              onClick={() => setClosing(true)}
              disabled={close.saving}
            >
              Завершить и списать расхождения
            </button>
          )}
        </>
      }
    >
      {take.loading && <Loading />}
      {take.error && <ErrorState message={take.error} />}
      {data && (
        <>
          <p className="muted">
            Начата {dateOnly(data.startedAt)}
            {data.progress && ` · посчитано ${data.progress.counted} из ${data.progress.total}`}
            {typeof data.diffSum === 'number' && (
              <>
                {' '}
                · расхождение {money(data.diffSum)} <Cur />
              </>
            )}
          </p>
          <div className="table-wrap">
            <table className="rtable">
              <thead>
                <tr>
                  <th>Позиция</th>
                  <th className="num">Ожидалось</th>
                  <th className="num" style={{ width: 130 }}>
                    Факт
                  </th>
                  <th className="num">Расхождение</th>
                  <th className="num">В деньгах</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.id}>
                    <td>
                      {l.name} <span className="muted">{UNIT_LABEL[l.unit] ?? l.unit}</span>
                    </td>
                    <td className="num">{count(l.qtyExpected)}</td>
                    <td className="num">
                      {readOnly ? (
                        l.qtyActual === null ? (
                          '—'
                        ) : (
                          count(l.qtyActual)
                        )
                      ) : (
                        <input
                          className="input"
                          type="number"
                          step="0.001"
                          min="0"
                          value={
                            drafts[l.stockItemId] ??
                            (l.qtyActual === null ? '' : String(l.qtyActual))
                          }
                          onChange={(e) =>
                            setDrafts((p) => ({ ...p, [l.stockItemId]: e.target.value }))
                          }
                          onBlur={(e) => void saveActual(l.stockItemId, e.target.value)}
                          placeholder="не посчитано"
                        />
                      )}
                    </td>
                    <td className="num">
                      {l.diffQty === null ? (
                        '—'
                      ) : (
                        <span style={l.diffQty < 0 ? { color: 'var(--red)' } : undefined}>
                          {l.diffQty > 0 ? '+' : ''}
                          {count(l.diffQty)}
                        </span>
                      )}
                    </td>
                    <td className="num">
                      {l.diffSum === null ? (
                        '—'
                      ) : (
                        <>
                          {l.diffSum > 0 ? '+' : ''}
                          {money(l.diffSum)} <Cur />
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {save.error && <div className="form-error">{save.error}</div>}
          {!readOnly && (
            <p className="muted" style={{ marginTop: 8 }}>
              Факт сохраняется при уходе из поля. Позиции без введённого факта считаются
              непосчитанными и при завершении не меняются.
            </p>
          )}
        </>
      )}

      {closing && (
        <ConfirmDialog
          title="Завершить инвентаризацию"
          message="Остатки посчитанных позиций будут приведены к факту, а расхождения зафиксированы движениями склада. Отменить это нельзя."
          busy={close.saving}
          error={close.error}
          onConfirm={() => void onCloseTake()}
          onCancel={() => setClosing(false)}
        />
      )}
    </Modal>
  )
}

// ─────────────────────────── Отчёты ───────────────────────────

interface SalesRow {
  menuItemId: string
  name: string
  qty: number
  revenue: number
  cost: number
  margin: number
  marginPercent: number | null
  share?: number
  cumulativeShare?: number
  group?: 'A' | 'B' | 'C'
}

interface SalesReport {
  items: SalesRow[]
  totals: {
    qty: number
    revenue: number
    cost: number
    margin: number
    marginPercent: number | null
    writeOffCost: number
  }
}

interface StockReportRow {
  id: string
  name: string
  unit: string
  qty: number
  minQty: number
  costPrice: number
  value: number
  isStopped: boolean
  isActive: boolean
  belowMin: boolean
}

interface StockReport {
  items: StockReportRow[]
  totals: { positions: number; value: number; belowMin: number; negative: number; stopped: number }
}

interface StockTakeReportRow {
  id: string
  status: 'open' | 'closed'
  startedAt: number
  closedAt: number | null
  counted: number
  mismatched: number
  diffSum: number
}

const DAY_MS = 24 * 60 * 60 * 1000

function ReportsSection(): React.JSX.Element {
  const query = useConfigQuery()
  // По умолчанию — последние 30 дней: типичный горизонт для маржи и ABC.
  const [from, setFrom] = useState(msToDateInput(Date.now() - 30 * DAY_MS))
  const [to, setTo] = useState(msToDateInput(Date.now()))
  const fromMs = dateInputToMs(from)
  // Конец дня, иначе заказы последних суток выпадут из периода.
  const toMs = dateInputToMs(to) + DAY_MS - 1
  const period = { ...query, from: fromMs, to: toMs }

  const abc = useFetch<SalesReport>('/v1/warehouse/reports/abc', period, [
    query?.clubId,
    fromMs,
    toMs
  ])
  const stock = useFetch<StockReport>('/v1/warehouse/reports/stock', query, [query?.clubId])
  const takes = useFetch<{
    items: StockTakeReportRow[]
    totals: { count: number; diffSum: number }
  }>('/v1/warehouse/reports/stock-takes', period, [query?.clubId, fromMs, toMs])

  const sales = abc.data
  return (
    <>
      <div className="toolbar">
        <div className="toolbar__left" style={{ gap: 8, display: 'flex', alignItems: 'center' }}>
          <label className="field__label" htmlFor="r-from">
            Период
          </label>
          <input
            id="r-from"
            className="input"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
          <span className="muted">—</span>
          <input className="input" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
      </div>

      <SectionHead title="Продажи и маржинальность" />
      {abc.loading && <Loading />}
      {abc.error && <ErrorState message={abc.error} />}
      {sales && sales.items.length === 0 && (
        <EmptyState message="За выбранный период продаж бара и кухни не было." />
      )}
      {sales && sales.items.length > 0 && (
        <>
          <div className="table-wrap">
            <table className="rtable">
              <thead>
                <tr>
                  <th>Позиция</th>
                  <th className="num">Продано</th>
                  <th className="num">Выручка</th>
                  <th className="num">Себестоимость</th>
                  <th className="num">Маржа</th>
                  <th className="num">Маржа, %</th>
                  <th className="num">Доля</th>
                  <th>ABC</th>
                </tr>
              </thead>
              <tbody>
                {sales.items.map((r) => (
                  <tr key={r.menuItemId}>
                    <td>{r.name}</td>
                    <td className="num">{count(r.qty)}</td>
                    <td className="num">
                      {money(r.revenue)} <Cur />
                    </td>
                    <td className="num">
                      {money(r.cost)} <Cur />
                    </td>
                    <td className="num">
                      <span style={r.margin < 0 ? { color: 'var(--red)' } : undefined}>
                        {money(r.margin)} <Cur />
                      </span>
                    </td>
                    <td className="num">
                      {r.marginPercent === null ? '—' : `${r.marginPercent}%`}
                    </td>
                    <td className="num">{r.share === undefined ? '—' : `${r.share}%`}</td>
                    <td>{r.group ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>Итого</td>
                  <td className="num">{count(sales.totals.qty)}</td>
                  <td className="num">
                    {money(sales.totals.revenue)} <Cur />
                  </td>
                  <td className="num">
                    {money(sales.totals.cost)} <Cur />
                  </td>
                  <td className="num">
                    {money(sales.totals.margin)} <Cur />
                  </td>
                  <td className="num">
                    {sales.totals.marginPercent === null ? '—' : `${sales.totals.marginPercent}%`}
                  </td>
                  <td className="num" />
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
          <p className="muted" style={{ marginTop: 8 }}>
            Себестоимость взята по текущим техкартам и закупочным ценам, а не по цене на момент
            продажи: снимка себестоимости в строках заказа пока нет, поэтому при заметном изменении
            закупок маржа задним числом переоценивается. Для сверки — списано со склада по этим
            заказам на {money(sales.totals.writeOffCost)} <Cur /> (журнал движений, цены
            зафиксированы в момент списания). ABC: A — позиции, дающие первые 80% выручки, B — до
            95%, C — остальные.
          </p>
        </>
      )}

      <SectionHead title="Остатки" />
      {stock.loading && <Loading />}
      {stock.error && <ErrorState message={stock.error} />}
      {stock.data && (
        <>
          <div className="toolbar">
            <div className="toolbar__left">
              <span className="muted">
                {stock.data.totals.positions} позиц. на {money(stock.data.totals.value)} <Cur />
                {stock.data.totals.belowMin > 0 && ` · ${stock.data.totals.belowMin} на минимуме`}
                {stock.data.totals.negative > 0 && ` · ${stock.data.totals.negative} в минусе`}
                {stock.data.totals.stopped > 0 && ` · ${stock.data.totals.stopped} на стопе`}
              </span>
            </div>
          </div>
          <div className="table-wrap">
            <table className="rtable">
              <thead>
                <tr>
                  <th>Позиция</th>
                  <th className="num">Остаток</th>
                  <th className="num">Минимум</th>
                  <th className="num">Себестоимость</th>
                  <th className="num">Стоимость остатка</th>
                </tr>
              </thead>
              <tbody>
                {stock.data.items
                  .filter((i) => i.isActive)
                  .map((i) => (
                    <tr key={i.id}>
                      <td>
                        {i.name}
                        {i.isStopped && <span className="muted"> · стоп</span>}
                      </td>
                      <td className="num">
                        <span style={i.belowMin ? { color: 'var(--red)' } : undefined}>
                          {count(i.qty)}
                        </span>{' '}
                        <span className="muted">{UNIT_LABEL[i.unit] ?? i.unit}</span>
                      </td>
                      <td className="num">{count(i.minQty)}</td>
                      <td className="num">
                        {money(i.costPrice)} <Cur />
                      </td>
                      <td className="num">
                        {money(i.value)} <Cur />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <SectionHead title="Инвентаризации за период" />
      {takes.loading && <Loading />}
      {takes.error && <ErrorState message={takes.error} />}
      {takes.data && takes.data.items.length === 0 && (
        <EmptyState message="За период инвентаризаций не было." />
      )}
      {takes.data && takes.data.items.length > 0 && (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>Начата</th>
                <th>Закрыта</th>
                <th>Статус</th>
                <th className="num">Посчитано</th>
                <th className="num">С расхождением</th>
                <th className="num">Расхождение</th>
              </tr>
            </thead>
            <tbody>
              {takes.data.items.map((t) => (
                <tr key={t.id}>
                  <td>{dateOnly(t.startedAt)}</td>
                  <td>{t.closedAt ? dateOnly(t.closedAt) : '—'}</td>
                  <td>{t.status === 'open' ? 'открыта' : 'закрыта'}</td>
                  <td className="num">{count(t.counted)}</td>
                  <td className="num">{count(t.mismatched)}</td>
                  <td className="num">
                    <span style={t.diffSum < 0 ? { color: 'var(--red)' } : undefined}>
                      {money(t.diffSum)} <Cur />
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={5}>Итого расхождений</td>
                <td className="num">
                  {money(takes.data.totals.diffSum)} <Cur />
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </>
  )
}
